import createClient, { type Client } from "openapi-fetch";
import type { paths } from "../generated/api/legacy-shop-schema";
import type { LegacyApiMiddlewareProps } from "./types";

/**
 * Instantiate the legacy commerce client inside apps/ecommerce-frontend with the correct baseUrl
 * @param baseUrl
 */
export const createLegacyApiClient = (baseUrl: string): Client<paths> => {
	return createClient<paths>({
		baseUrl,
	});
};

// Needed for calls that send the basket only in the request body; others also match via the `basket` query param
const BASKET_ENDPOINTS = new Set([
	"addtobasket",
	"addvoucher",
	"basketcontent",
	"basketcustomer",
	"basketdata",
	"basketdefinition",
	"basketdelete",
	"basketentry",
	"basketinfo",
	"basketunlock",
	"changedatebasketentry",
	"checkstate",
	"copybasket",
	"customerdata",
	"deletevoucher",
	"deselectentry",
	"removefrombasket",
	"reservebasket",
	"startpaymentprocess",
	"stoppaymentprocess",
]);

const isBasketRequest = (url: URL) =>
	url.searchParams.has("basket") ||
	BASKET_ENDPOINTS.has(url.pathname.split("/").pop() ?? "");

export const createLegacyApiMiddleware = ({
	getLocale,
	getCredentials,
	setCredentials,
	setNotice,
	scrollToTop,
	onSearchError,
	onBasketAccessDenied,
	onBasketAccessGranted,
}: LegacyApiMiddlewareProps) => {
	return {
		async onRequest({ request }: { request: Request }) {
			const credentials = getCredentials();
			const langCode = getLocale();

			if (!request.headers.get("content-type")) {
				request.headers.set("content-type", "application/json");
			}

			// tokens:
			if (credentials.accessToken) {
				request.headers.set(
					"Authorization",
					`Bearer ${credentials.accessToken}`,
				);
			}
			if (credentials.refreshToken) {
				request.headers.set("Authorization-Refresh", credentials.refreshToken);
			}

			request.headers.set("accept-language-master", langCode || "de");
			request.headers.set("X-Process-Token", credentials.clientHash);

			return request;
		},
		async onResponse({
			response,
			request,
		}: {
			response: Response;
			request: Request;
		}) {
			const setToken = response.headers.get("set-token");
			const setRefreshToken = response.headers.get("set-refreshtoken");
			setCredentials({ accessToken: setToken, refreshToken: setRefreshToken });

			const resClone = response.clone();
			const responseJson = await resClone.json();
			const url = new URL(request.url);
			const path = url.pathname;

			if (response.ok && !responseJson.error && isBasketRequest(url)) {
				onBasketAccessGranted?.({ anonymous: !responseJson.userinfo?.email });
			}

			// 200 handling:
			if (response.ok) {
				const formattedError = {
					details: responseJson.error?.messages,
					status: response.status,
					code: responseJson.error?.code,
					class: responseJson.error?.class,
				};

				// throw error as JSON directly into the catch of the caller:
				if (responseJson.error) {
					throw JSON.stringify(formattedError);
				} else if (responseJson.notices?.length) {
					const excludedCodes = [2111, 2112, 2102, 2101, 1021, 1022];

					responseJson.notices.forEach((notice: any) => {
						if (notice.code && !excludedCodes.includes(notice.code)) {
							setNotice({ ...notice, status: response.status });
						}
					});

					const isEmpty = !responseJson.data || responseJson.data?.length === 0;

					if (isEmpty && path !== "startpaymentprocess") {
						const noticeDetails = responseJson.notices
							.map((n: any) => n.message)
							.join("\n");

						throw JSON.stringify({
							details: noticeDetails,
							status: response.status,
							code: response.status,
						});
					}
				}
				return response;
			} else {
				// everything else besides 200:
				if (response.status === 403 && isBasketRequest(url)) {
					onBasketAccessDenied?.({ anonymous: !responseJson.userinfo?.email });
				}

				if (
					responseJson.error?.class ===
					"MicroserviceUsageCore\\Domain\\Exception\\RelocateException"
				) {
					if (
						responseJson.error.code === 1001 &&
						(window as any).keycloakReady
					) {
						window.location.href = (window as any).keycloak.createLoginUrl();
					} else if (responseJson.error.code === 1002) {
						window.location.href = responseJson.error.additionalinfo;
					} else {
						scrollToTop();
						throw JSON.stringify({
							details: responseJson.error.message, // + (responseJson.error.class ? " at " + responseJson.error.class : ""),
							status: response.status,
							code: responseJson.error.code,
							additionalInfo: responseJson.error.additionalInfo,
						});
					}
				} else {
					if (responseJson.error) {
						scrollToTop();
						if (
							(responseJson.error.code === 1000 ||
								responseJson.error.code === 1001 ||
								responseJson.error?.class ===
									"MicroserviceUsageCore\\Domain\\Exception\\ParameterException") &&
							(window as any).keycloakReady &&
							responseJson.error?.class !==
								"App\\Shop\\Domain\\Exception\\BasketValidationException"
						) {
							onSearchError("?error=true");
						}
						throw JSON.stringify({
							details: responseJson.error.message, // + (responseJson.error.code ? " at " + responseJson.error.class : ""),
							status: response.status,
							code: responseJson.error.code,
							class: responseJson.error.class,
						});
					} else {
						throw JSON.stringify(responseJson);
					}
				}
			}
		},
	};
};
